import { put, takeLatest, call } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  PatientApi,
  Patient,
  PatientListResponse,
  PatientAnalyticsResponse,
  MedicalInfoUpdate,
} from '../../api/services/patients';
import {
  fetchPatients,
  fetchPatientsSuccess,
  fetchPatientsFailure,
  fetchPatientDetails,
  fetchPatientDetailsSuccess,
  fetchPatientDetailsFailure,
  createPatient,
  createPatientSuccess,
  createPatientFailure,
  updatePatient,
  updatePatientSuccess,
  updatePatientFailure,
  updateMedicalInfo,
  updateMedicalInfoSuccess,
  updateMedicalInfoFailure,
  deletePatient,
  deletePatientSuccess,
  deletePatientFailure,
  fetchPatientAnalytics,
  fetchPatientAnalyticsSuccess,
  fetchPatientAnalyticsFailure,
} from '../slices/patientsSlice';
import { PatientFormData } from '../../types/patient';
import { parseDjangoError, getErrorMessage, type ErrorResponse } from '../../utils/errorParser';

// Fetch all patients
function* fetchPatientsSaga() {
  try {
    const response: PatientListResponse | Patient[] = yield call(PatientApi.getAll);

    // Handle both paginated response with results field and direct array response
    if (Array.isArray(response)) {
      // Direct array response
      yield put(fetchPatientsSuccess(response));
    } else if (response && 'results' in response && response.results) {
      // Paginated response
      yield put(fetchPatientsSuccess(response.results));
    } else {
      console.error('Unexpected API response format:', response);
      yield put(fetchPatientsFailure('Failed to fetch patients data'));
    }
  } catch (error: any) {
    console.error('Error fetching patients:', error);
    const errorMessage = getErrorMessage(error);
    yield put(fetchPatientsFailure(errorMessage));
  }
}

function* fetchPatientDetailsSaga(action: PayloadAction<number | string>) {
  try {
    const response: PatientListResponse | Patient = yield call(PatientApi.getById, action.payload);

    // Handle both paginated response with results field and direct Patient response
    let patient: Patient | null = null;

    if (response && 'results' in response && Array.isArray(response.results)) {
      // Paginated response - take the first result
      patient = response.results.length > 0 ? response.results[0] : null;
    } else if (response && 'id' in response) {
      // Direct Patient response
      patient = response as Patient;
    }

    if (patient) {
      yield put(fetchPatientDetailsSuccess(patient));
    } else {
      yield put(
        fetchPatientDetailsFailure('Patient not found. Please check if the patient exists.')
      );
    }
  } catch (error: any) {
    console.error('Error fetching patient details:', error);
    const errorMessage = getErrorMessage(error);
    yield put(
      fetchPatientDetailsFailure(
        errorMessage || 'Unable to fetch patient details. Please try again later.'
      )
    );
  }
}

// Create patient
function* createPatientSaga(
  action: PayloadAction<{ data: PatientFormData; cb: (patientId: string) => void }>
) {
  try {
    const patient = action.payload.data;
    let formattedDob = null;
    if (patient.dateOfBirth) {
      try {
        const date = new Date(patient.dateOfBirth);
        formattedDob = date.toISOString().split('T')[0]; // Gets YYYY-MM-DD format
      } catch (error) {
        console.error('Error formatting date of birth:', error);
      }
    }
    // Clean phone number by removing spaces
    const cleanPhone = (patient.phone || '').replace(/\s/g, '');

    const medicalInfo: Record<string, string | number> = {
      phone: cleanPhone,
      cur_medications: patient.medications || '',
      medical_history: patient.medicalHistory || '',
      prev_treatments: patient.previousTreatments || '',
      emergency_contact: patient.emergencyContactName || '',
      emergency_contact_email: patient.emergencyContactEmail || '',
      patient_relation: patient.emergencyContactRelation || '',
      patient_profile: '',
      emergency_phone: patient.emergencyContactPhone || '',
      emergency_notes: patient.emergencyContactNotes || '',
      insurance_provider: patient.insuranceProvider || '',
      insurance_group: patient.insuranceGroup || '',
      document_loc: '',
      allergies: patient.allergies || '',
      conditions: patient.primaryCondition || '',
    };

    if (patient.medicareNumber && patient.medicareNumber.trim() !== '') {
      medicalInfo.medicare = patient.medicareNumber;
    }

    if (patient.insuranceNumber && patient.insuranceNumber.trim() !== '') {
      const parsedInsuranceNumber = Number(patient.insuranceNumber);
      if (!Number.isNaN(parsedInsuranceNumber)) {
        medicalInfo.insurance_number = parsedInsuranceNumber;
      }
    }

    const backendPatient = {
      email: patient.email,
      first_name: patient.firstName,
      last_name: patient.lastName,
      address: patient.address,
      urn: patient.urn || '',
      phone: cleanPhone,
      dob: formattedDob,
      gender: patient.gender,
      occupation: patient.occupation || '',
      referred_by: patient.referredBy || '',
      language: 'EN',
      communication_preference: 'EMAIL',
      doctor_notes: patient.doctorNotes || '',
      medical_info: medicalInfo,
    };
    const response: Patient = yield call(
      PatientApi.createPatient,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      backendPatient as any
    );
    if (response) {
      yield put(createPatientSuccess());
      // Refresh the patients list
      yield put(fetchPatients());
      console.log('create patient response', response);

      if (action.payload.cb) {
        action.payload.cb(response?.id?.toString() || '');
      }
    } else {
      yield put(
        createPatientFailure({
          message: 'Failed to create patient. Please try again.',
        })
      );
    }
  } catch (error) {
    const parsedError = parseDjangoError(error as ErrorResponse);
    yield put(
      createPatientFailure({
        message: parsedError.message,
        fieldErrors: parsedError.fieldErrors,
        status: parsedError.status,
      })
    );
  }
}

// Update patient
function* updatePatientSaga(
  action: PayloadAction<{ id: number | string; data: Partial<PatientFormData> }>
) {
  try {
    const { id, data: formData } = action.payload;
    if (!formData) return;
    const updateData: Record<string, any> = {};
    const medicalInfo: Record<string, any> = {};

    const addField = (key: string, value: unknown) => {
      if (typeof value === 'undefined') return;
      updateData[key] = value === '' ? null : value;
    };

    const addMedicalField = (key: string, value: unknown) => {
      if (typeof value === 'undefined') return;
      medicalInfo[key] = value === '' ? null : value;
    };

    if (typeof formData.email !== 'undefined') {
      addField('email', formData.email);
    }
    if (typeof formData.firstName !== 'undefined') {
      addField('first_name', formData.firstName);
    }
    if (typeof formData.lastName !== 'undefined') {
      addField('last_name', formData.lastName);
    }
    if (typeof formData.address !== 'undefined') {
      addField('address', formData.address);
    }
    if (typeof formData.urn !== 'undefined') {
      addField('urn', formData.urn);
    }
    if (typeof formData.gender !== 'undefined') {
      addField('gender', formData.gender);
    }
    if (typeof formData.occupation !== 'undefined') {
      addField('occupation', formData.occupation);
    }
    if (typeof formData.primaryCondition !== 'undefined') {
      addMedicalField('conditions', formData.primaryCondition);
    }
    if (typeof formData.doctorNotes !== 'undefined') {
      addField('doctor_notes', formData.doctorNotes);
    }

    if (typeof formData.phone !== 'undefined') {
      if (formData.phone === '' || formData.phone === null) {
        addField('phone', null);
        addMedicalField('phone', null);
      } else {
        const rawPhone = formData.phone ?? '';
        const cleanPhone = String(rawPhone).replace(/\s/g, '');
        addField('phone', cleanPhone);
        addMedicalField('phone', cleanPhone);
      }
    }

    if (typeof formData.dateOfBirth !== 'undefined') {
      if (formData.dateOfBirth === '' || formData.dateOfBirth === null) {
        addField('dob', null);
      } else {
        try {
          const date = new Date(String(formData.dateOfBirth));
          addField('dob', date.toISOString().split('T')[0]);
        } catch (error) {
          console.error('Error formatting date of birth:', error);
        }
      }
    }

    if (typeof formData.medicareNumber !== 'undefined') {
      if (formData.medicareNumber === '' || formData.medicareNumber === null) {
        addMedicalField('medicare', null);
      } else {
        const parsedMedicare = Number(formData.medicareNumber);
        addMedicalField('medicare', Number.isNaN(parsedMedicare) ? null : parsedMedicare);
      }
    }

    if (typeof formData.allergies !== 'undefined') {
      addMedicalField('allergies', formData.allergies);
    }

    if (Object.keys(medicalInfo).length > 0) {
      updateData.medical_info = medicalInfo;
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const response: PatientListResponse = yield call(PatientApi.updatePatient, id, updateData);
    if (response) {
      yield put(updatePatientSuccess());
      // Refresh patient details and list
      yield put(fetchPatientDetails(id));
      yield put(fetchPatients());
    } else {
      yield put(
        updatePatientFailure({
          message: 'Failed to update patient. Please try again.',
        })
      );
    }
  } catch (error) {
    const parsedError = parseDjangoError(error as ErrorResponse);
    yield put(
      updatePatientFailure({
        message: parsedError.message,
        fieldErrors: parsedError.fieldErrors,
        status: parsedError.status,
      })
    );
  }
}

// Update medical info
function* updateMedicalInfoSaga(
  action: PayloadAction<{ id: number | string; data: MedicalInfoUpdate }>
) {
  try {
    const { id, data: medicalInfo } = action.payload;

    const response: PatientListResponse = yield call(PatientApi.updateMedicalInfo, id, medicalInfo);
    if (response) {
      yield put(updateMedicalInfoSuccess());
      // Refresh patient details and list
      yield put(fetchPatientDetails(id));
      yield put(fetchPatients());
    } else {
      yield put(updateMedicalInfoFailure('Failed to update medical information'));
    }
  } catch (error: any) {
    const errorMessage = getErrorMessage(error);
    yield put(updateMedicalInfoFailure(errorMessage));
  }
}

// Delete patient
function* deletePatientSaga(action: PayloadAction<number | string>) {
  try {
    yield call(PatientApi.deletePatient, action.payload);
    yield put(deletePatientSuccess());
    // Refresh the patients list
    yield put(fetchPatients());
  } catch (error: any) {
    const errorMessage = getErrorMessage(error);
    yield put(deletePatientFailure(errorMessage));
  }
}

// Fetch patient analytics
function* fetchPatientAnalyticsSaga() {
  try {
    const response: PatientAnalyticsResponse = yield call(PatientApi.getAnalytics);
    if (response && response.data) {
      yield put(fetchPatientAnalyticsSuccess(response.data));
    } else {
      yield put(fetchPatientAnalyticsFailure('Failed to fetch patient analytics'));
    }
  } catch (error: any) {
    const errorMessage = getErrorMessage(error);
    yield put(fetchPatientAnalyticsFailure(errorMessage));
  }
}

// Root saga combining all patients sagas
export function* watchPatientsActions() {
  yield takeLatest(fetchPatients.type, fetchPatientsSaga);
  yield takeLatest(fetchPatientDetails.type, fetchPatientDetailsSaga);
  yield takeLatest(createPatient.type, createPatientSaga);
  yield takeLatest(updatePatient.type, updatePatientSaga);
  yield takeLatest(updateMedicalInfo.type, updateMedicalInfoSaga);
  yield takeLatest(deletePatient.type, deletePatientSaga);
  yield takeLatest(fetchPatientAnalytics.type, fetchPatientAnalyticsSaga);
}
